/**
 * Unified Variable Definition Types
 *
 * This file provides a single, unified type system for defining variables/fields
 * across the application. Previously, this concept was duplicated in:
 * - FieldDefinition (FieldDefinitionEditor)
 * - TemplateVariableDefinition (TemplateVariableEditor)
 * - AgentInputVariable (AgentModal)
 * - FlowInput (StaticInputEditor)
 * - ColumnDefinition (TargetListItemsTab)
 *
 * Now all of these use VariableDefinitionModel as the single source of truth.
 */

import type { TemplateVariableType } from './template-variable.types';
import type { VariableTypeKey } from './variable-type-registry.types';
import { VariableFieldType } from './variable-type-registry.types';

// Re-export TemplateVariableType for convenience (original is in template-variable.types.ts)
export type { TemplateVariableType };

/**
 * Unified variable/field definition model.
 *
 * This interface combines all features needed across different contexts:
 * - Template variables (email, messaging)
 * - Automation flow inputs
 * - AI agent input variables
 * - Target list column definitions
 * - Custom field definitions
 */
export interface VariableDefinitionModel {
	/** The variable/field name (used in {{name}} syntax) */
	name: string;

	/** Human-readable label for display (defaults to name if not provided) */
	label?: string;

	/** The data type of the variable */
	type: VariableFieldType;

	/** Whether this variable is required */
	required?: boolean;

	/** Default value to use if not provided */
	defaultValue?: unknown;

	/** Human-readable description of what this variable represents */
	description?: string;

	/** For ARRAY types: the type of items in this array */
	arrayType?: VariableTypeKey;

	/** For OBJECT types: the type of this object */
	objectType?: VariableTypeKey;

	/** Whether this field is the primary key (for column definitions) */
	isPrimaryKey?: boolean;

	/** Whether this field value cannot be changed after initial set */
	immutable?: boolean;

	/**
	 * AGENT OUTPUT variables only: the name of an INPUT variable to fall back
	 * to. When post-call extraction does not capture this output (the customer
	 * never stated or changed it), the runtime fills it from the named input
	 * variable's runtime value instead — e.g. "the email we started the call
	 * with, unless the customer corrected it" — without needing a flow-level
	 * coalesce. Ignored for input variables, template variables and column
	 * definitions.
	 */
	fallbackFromInput?: string;
}

/**
 * Extended variable definition with an ID field.
 * Used for storage where unique identification is needed (e.g., target list columns).
 */
export interface VariableDefinitionWithIdModel extends VariableDefinitionModel {
	/** Unique identifier for this definition */
	id: string;
}

/**
 * Converts a TemplateVariableType string to VariableFieldType enum.
 */
export function templateTypeToFieldType(
	type: TemplateVariableType | string,
): VariableFieldType {
	switch (type) {
		case 'string':
		case 'text':
			return VariableFieldType.STRING;
		case 'number':
			return VariableFieldType.NUMBER;
		case 'boolean':
			return VariableFieldType.BOOLEAN;
		case 'date':
			return VariableFieldType.DATE;
		case 'array':
			return VariableFieldType.ARRAY;
		case 'object':
			return VariableFieldType.OBJECT;
		case 'email':
			return VariableFieldType.EMAIL;
		case 'phone':
			return VariableFieldType.PHONE;
		case 'currency':
			return VariableFieldType.CURRENCY;
		default:
			return VariableFieldType.STRING;
	}
}

/**
 * Converts a VariableFieldType enum to the legacy TemplateVariableType string.
 */
export function fieldTypeToTemplateType(
	type: VariableFieldType,
): TemplateVariableType {
	switch (type) {
		case VariableFieldType.STRING:
		case VariableFieldType.EMAIL:
		case VariableFieldType.PHONE:
		case VariableFieldType.CURRENCY:
			return 'string';
		case VariableFieldType.NUMBER:
			return 'number';
		case VariableFieldType.BOOLEAN:
			return 'boolean';
		case VariableFieldType.DATE:
			return 'date';
		case VariableFieldType.ARRAY:
			return 'array';
		case VariableFieldType.OBJECT:
			return 'object';
		default:
			return 'string';
	}
}
